/* ==========================================================================
   SearcherFli > Client > Submit > Url
   ========================================================================== */
const {
    ORIGIN_AIRPORT_CODE,
    DESTINATION_AIRPORT_CODE,
    LOB,
    PRODUCT_TYPE,
    DEPARTURE_DATE_STR,
    RETURN_DATE_STR,
    CABIN_CLASS,
    FAMILY_DISCOUNT,
    ADULTS,
    CHILDREN,
    BABIES,
    HASH_ROOMS,
    AIR,
    ITINERARY_HASH,
    COMPANY,
    AERIAL_COMPANY_NAME,
    DIRECT,
    TOUR_OPERATOR,
    REFUNDABLE,
    ETHNIC
} = require('../constants');

const { isOneway, isRoundtrip, formatDate, isOpenjaw } = require('../../helpers');

/* Build line of bussines */
function buildLob() {
    return `${LOB}=Flights`;
}

/* Build product type */
function buildProductType() {
    return `${PRODUCT_TYPE}=Flight`;
}

/* Build origin */
function buildOrigin(value) {
    return `${ORIGIN_AIRPORT_CODE}=${encodeURIComponent(value)}`;
}

/* Build Destination */
function buildDestination(value) {
    return `${DESTINATION_AIRPORT_CODE}=${encodeURIComponent(value)}`;
}

/* Build DepartureDateStr */
function buildDepartureDateStr(departureDate) {
    return `${DEPARTURE_DATE_STR}=${encodeURIComponent(departureDate)}`;
}

/* Build ReturnDateStr */
function buildReturnDateStr(returnDate) {
    return `${RETURN_DATE_STR}=${encodeURIComponent(returnDate)}`;
}

/* Build parameter passengers */
function buildPassenger({ adults, children, babies }) {
    return `${HASH_ROOMS}=${encodeURIComponent(
        `${ADULTS.repeat(adults)}${CHILDREN.repeat(children)}${BABIES.repeat(babies)}`
    )}`;
}

/* Build ItineraryHash */
function buildItineraryHash(itinerary) {
    return `${ITINERARY_HASH}=${encodeURIComponent(itinerary)}`;
}

/* Build Classes */
function buildClasses(classes) {
    return classes && classes.value ? `${CABIN_CLASS}=${encodeURIComponent(classes.value)}` : false;
}

/* Build Discount */
function buildDiscount(discount) {
    return discount && discount.value ? `${FAMILY_DISCOUNT}=${encodeURIComponent(discount.value)}` : false;
}

/* Build Airline */
function buildAirline(airline) {
    return airline && airline.value ? `${COMPANY}=${airline.value}&${AERIAL_COMPANY_NAME}=${airline.text}` : false;
}

/* Build parameter options */
const DEFAULT_NAMES = [DIRECT, TOUR_OPERATOR, REFUNDABLE, ETHNIC];
function buildOptions(options, optionsConfig) {
    if (!options) {
        return false;
    }

    const optionNames = optionsConfig.map((x, i) => x.name || DEFAULT_NAMES[i]);

    return options
        .map((option, i) => (option ? `${optionNames[i]}=${options[i]}` : false))
        .filter(item => item)
        .join('&');
}

/* Build passengers names */
function buildPaxesNames(paxesNames) {
    if (!paxesNames) return false;

    return `${paxesNames}`;
}

/* Build travel policy */
function buildTravelPolicy(travelPolicy) {
    if (!travelPolicy) return false;

    return travelPolicy;
}

/* Build travel policy */
function buildDirectFlights(directFlights) {
    if (!directFlights) return false;

    return directFlights;
}

/* Build URL */
function buildUrl(instance) {
    const { product } = instance.store.select('data');
    if (isOneway(product)) return buildUrlOneway(instance);
    if (isRoundtrip(product)) return buildUrlRountrip(instance);
    if (isOpenjaw(product)) return buildOpenjaw(instance);

    return '';
}

function buildUrlRountrip(instance) {
    const { url, options: optionsConfig } = instance.config;
    const {
        origin,
        destination,
        calendar,
        passengers,
        paxesNames = '',
        travelPolicy = '',
        directFlights = false,
        classes = {},
        discount = {},
        airline = {},
        options = false
    } = instance.store.select('data');

    /* Format dates */
    const departureDate = formatDate(calendar[0]);
    const returnDate = formatDate(calendar[1]);

    /* Itinerary hash */
    const itinerary = [
        `${origin.value}.${AIR}.${destination.value}.${AIR}.${departureDate}`,
        `${destination.value}.${AIR}.${origin.value}.${AIR}.${returnDate}`
    ].join('#');

    /* Build array with params */
    const params = [
        buildLob(),
        buildProductType(),
        buildOrigin(origin.value),
        buildDestination(destination.value),
        buildDepartureDateStr(departureDate),
        buildReturnDateStr(returnDate),
        buildPassenger(passengers),
        buildItineraryHash(itinerary),
        buildClasses(classes),
        buildDiscount(discount),
        buildAirline(airline),
        buildOptions(options, optionsConfig),
        buildPaxesNames(paxesNames),
        buildTravelPolicy(travelPolicy),
        buildDirectFlights(directFlights)
    ];

    return `${url}?${params.filter(item => item).join('&')}`;
}

function buildUrlOneway(instance) {
    const { url, options: optionsConfig } = instance.config;
    const {
        origin,
        destination,
        calendar,
        passengers,
        paxesNames = '',
        travelPolicy = '',
        directFlights = '',
        classes = {},
        discount = {},
        airline = {},
        options = false
    } = instance.store.select('data');

    /* Format dates */
    const departureDate = formatDate(calendar[0] || calendar);

    /* Itinerary hash */
    const itinerary = `${origin.value}.${AIR}.${destination.value}.${AIR}.${departureDate}`;

    /* Build array with params */
    const params = [
        buildLob(),
        buildProductType(),
        buildOrigin(origin.value),
        buildDestination(destination.value),
        buildDepartureDateStr(departureDate),
        buildPassenger(passengers),
        buildItineraryHash(itinerary),
        buildClasses(classes),
        buildDiscount(discount),
        buildAirline(airline),
        buildOptions(options, optionsConfig),
        buildPaxesNames(paxesNames),
        buildTravelPolicy(travelPolicy),
        buildDirectFlights(directFlights)
    ];

    return `${url}?${params.filter(item => item).join('&')}`;
}

function buildOpenjaw(instance) {
    const { url, options: optionsConfig } = instance.config;
    const {
        passengers,
        paxesNames = '',
        travelPolicy = '',
        directFlights = false,
        classes = {},
        discount = {},
        airline = {},
        options = false
    } = instance.store.select('data');
    const { rows } = instance.store.select('openjaw');

    /* Itinerary hash */
    const itinerary = rows
        .map(
            item =>
                `${item.origin.value}.${AIR}.${item.destination.value}.${AIR}.${formatDate(
                    item.calendar[0] || item.calendar
                )}`
        )
        .join('#');

    /* Build array with params */
    const params = [
        buildLob(),
        buildProductType(),
        buildOrigin(rows[0].origin.value),
        buildDestination(rows[0].destination.value),
        buildDepartureDateStr(formatDate(rows[0].calendar)),
        buildReturnDateStr(formatDate(rows[1].calendar)),
        buildPassenger(passengers),
        buildItineraryHash(itinerary),
        buildClasses(classes),
        buildDiscount(discount),
        buildAirline(airline),
        buildOptions(options, optionsConfig),
        buildPaxesNames(paxesNames),
        buildTravelPolicy(travelPolicy),
        buildDirectFlights(directFlights)
    ];

    return `${url}?${params.filter(item => item).join('&')}`;
}

module.exports = {
    buildUrl
};
